import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import StyledDrawer from "../../../../../components/StyledDrawer";
import Text from "../../../../../components/Text";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useReviewTask } from "../../../../../queries/collaboration/tasks/use-review-task";
import { TaskReviewedReviewedStatusEnum } from "../../../../../services";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { getXtenantId, showToastOnApiCall } from "../../../../../utils/utils";

interface ReviewTaskDrawerProps {
  disclosureHost: DisclosureHost;
}

type FormikValues = {
  reviewNote: string;
};

const validationSchema = Yup.object().shape({
  reviewNote: Yup.string().trim().required("Review note is required"),
});

const ReviewTaskDrawer = ({ disclosureHost }: ReviewTaskDrawerProps) => {
  const { mutateAsync: reviewTask } = useReviewTask();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const task = disclosureHost.data ?? {};

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const handleSubmit = async (values: FormikValues) => {
    showToastOnApiCall({
      apiCall: reviewTask({
        taskReviewed: {
          taskUuid: task?.uuid,
          reviewedStatus: TaskReviewedReviewedStatusEnum.Reviewed,
          reviewedNote: values.reviewNote,
          reviewedDate: dayjs(new Date()).toISOString(),
        },
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        handleClose();
      },
    });
  };

  const formik = useFormik<FormikValues>({
    initialValues: { reviewNote: "" },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <StyledDrawer
      open={disclosureHost.opened}
      onClose={handleClose}
      title="Mark as Reviewed"
      w="44vw"
      bottomFixedAction
      drawerAction={
        <Flex gap={2}>
          <Button
            type="button"
            variant="outlined"
            disabled={formik.isSubmitting}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            onClick={formik.submitForm}
          >
            Mark as Reviewed
          </Button>
        </Flex>
      }
    >
      <Flex flexDirection={"column"} gap={3} width={"100%"}>
        <Box>
          <Text fontWeight={600} mb={1}>
            Patient Resolution Date
          </Text>
          <Text>
            {task?.resolvedDate ? formatDateTime(task.resolvedDate) : "-"}
          </Text>
        </Box>

        <Box>
          <Text fontWeight={600} mb={1}>
            Patient Resolution Notes
          </Text>
          <Text>{task?.description || "-"}</Text>
        </Box>

        <CustomTextArea
          label="Review Note"
          placeholder="Enter Review Note..."
          value={formik.values.reviewNote}
          onChange={(e) => formik.setFieldValue("reviewNote", e.target.value)}
          minRows={5}
          required
          error={formik.touched.reviewNote && (formik.errors.reviewNote as any)}
        />
      </Flex>
    </StyledDrawer>
  );
};

export default ReviewTaskDrawer;
